import { useRef } from 'react';
import { useLocation } from 'react-router';
import { professionalTabBase } from '@modules/professional/util/professional-tab-base';

import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { useFeature } from '~/modules/root/hook/use-feature';
import { useEditPermissions } from '~/modules/user';
import { getSubRoutes } from '@modules/professional/component/sub-route-navigation/utils';
import type { ProfessionalInitialResult } from '@modules/professional/hook/use-professional-initial-route-data';
import { getActiveTabLink } from '~/ui-rocco/pages/professional-detail/utils/subnav-active';
import { buildProfessionalNavItems } from '~/ui-rocco/pages/professional-detail/utils/professional-nav-items';
import { useTabScrollAnchor } from '~/ui-rocco/pages/professional-detail/hooks/use-tab-scroll-anchor';
import {
    useProfileRailStuck,
    useReportProfileRailDock,
} from '~/ui-rocco/pages/professional-detail/hooks/use-profile-rail-dock';
import { useBandOverDark } from '~/ui-rocco/pages/professional-detail/hooks/use-band-over-dark';
import { ProfessionalContactTabButton } from './ProfessionalContactTabButton';
import { ProfileFamilyLine } from './ProfileFamilyLine';
import { useProfessionalLayoutData } from '@modules/professional/hook/use-professional-layout-data';
import { navPill } from '~/ui-rocco/components/nav-pill';
import { LensSwitch } from '~/ui-rocco/components/LensSwitch';
import { RAIL_TABS_BAND, RAIL_TABS_TOP } from '../profile-rail';

/** Match the Figma's tab labels (legacy uses Profile). */
const LABEL_OVERRIDES: Partial<Record<string, string>> = {
    Profile: 'Overview',
};

/**
 * Desktop pill tab bar directly under the banner — the professional's
 * available tabs as rounded pills, the active tab a filled pill. It is sticky,
 * so it takes the dark palette whenever a dark section is passing beneath it
 * rather than per tab. Lives in the persistent layout so it renders once and
 * never re-flashes on tab nav. Mobile uses the underline
 * {@link import('./ProfessionalSubNav').ProfessionalSubNav} instead.
 */
export const ProfessionalDesktopSubNav = () => {
    const layoutData = useProfessionalLayoutData();
    const { pathname } = useLocation();
    const imageLabelSearchEnabled = useFeature('image_label_search_profile');
    const initial = layoutData?.initial;
    // Editors with Products allowed but no products yet still need the Products
    // tab to add their first product — mirror the legacy nav's edit permission.
    const { canEditProducts } = useEditPermissions(initial?.ID ?? 0);

    // Switching tabs leaves the scroll position alone — see useTabScrollAnchor.
    const navRef = useRef<HTMLElement>(null);
    useTabScrollAnchor(navRef);
    useReportProfileRailDock(navRef);
    // The band is sticky, so its palette follows what is passing under it
    // rather than which tab is open.
    const overDark = useBandOverDark(navRef);
    // The band carries no ground of its own until it locks to the top.
    const stuck = useProfileRailStuck();

    if (!initial) return null;

    const currentPath = pathname.replace(/\/+$/, '');
    // Preview-aware: under the Business Manager iframe the tabs must link
    // within /professional-preview, not out to the public site.
    const base = professionalTabBase(initial.URLSegment, currentPath);
    const professional = initial as unknown as ProfessionalInitialResult;
    // Match prod's tab set: pass linked-profile locations (Contact) and the
    // image-label flag (Spaces) through, exactly as the legacy nav does.
    const items = getSubRoutes(
        professional,
        canEditProducts,
        layoutData?.linkedProfiles?.linkedProfilesList,
        imageLabelSearchEnabled,
    );
    const navItems = buildProfessionalNavItems(items, {
        hasDownloads: professional.HasDownloads,
    });

    const tabLinks = navItems.map((item) =>
        `${base}${item.link}`.replace(/\/+$/, ''),
    );
    const activeTabLink = getActiveTabLink(tabLinks, currentPath);

    return (
        <nav
            ref={navRef}
            // Background on the full-width nav (the dark theme band must run
            // edge to edge); the pills cap at the centred 1920 container.
            //
            // The band locks under the brand badge and the page scrolls behind
            // it, frosted — the same treatment as the category pill bar. The
            // translucent fill is what the blur samples; over an opaque
            // background there would be nothing to show through. The site
            // header is in flow and scrolls away, so the offset answers to the
            // badge rather than to a header height.
            className={cn(
                // On the line the other detail pages give their trail: the
                // 8px lifts the pills' top edge to 32px under the banner.
                'md:sticky md:z-[115] md:mt-[0.5rem]',
                RAIL_TABS_TOP,
                // Nothing of its own until it locks to the top: while the band
                // is still travelling it sits over the banner and belongs to
                // the photograph.
                //
                // Locked, it is frosted and tinted to whichever ground it is
                // over. The fill is mostly opaque on purpose: fully
                // transparent, the band came out a shade lighter than the page
                // and read as a pale stripe across it, with the white pane
                // marking the current tab lost inside. At this weight the
                // photograph moving under it is legible as blur without the
                // band itself becoming a colour of its own.
                'transition-colors duration-200',
                // Blur only, no saturate: saturating the backdrop lifted the
                // band brighter than the page it sits on and it read as a
                // white stripe. The fill itself is an inline style — see
                // below.
                stuck && 'backdrop-blur-[20px]',
                // The pills' keyboard ring reads against the band's own fill,
                // which is near-black only once locked over a dark section.
                stuck && overDark && 'focus-ring-on-dark',
            )}
            /**
             * Before it locks, the band is simply the page: the same grey the
             * summary below it carries, so it reads as part of the page rather
             * than as a bar sitting on it.
             *
             * NOT `transparent`. The app root paints itself white, so a
             * transparent band showed white — a bright stripe between the
             * banner and the grey page, which is the opposite of invisible.
             *
             * Locked, it takes the frosted fill. That is weighted to the
             * page's own colour rather than to whatever is passing under it:
             * at 0.8 the band took its tone from the backdrop — the banner's
             * dark edge and the grey page averaging out lighter than either —
             * which is the pale stripe it was made solid to avoid.
             *
             * Inline rather than a class because it is a value Tailwind has to
             * generate, and a newly written arbitrary colour did not emit here:
             * the class sat on the element with no rule behind it and nothing
             * anywhere said so.
             */
            style={{
                backgroundColor: stuck
                    ? overDark
                        ? 'rgba(24, 24, 24, 0.95)'
                        : 'rgba(245, 245, 247, 0.95)'
                    : // The page's own ground, which the band wears until it
                      // locks to the top. Inline, so it reads `--ground`
                      // directly rather than through a utility.
                      'var(--ground)',
            }}
        >
            <div
                className={cn(
                    'mx-auto flex w-full max-w-[120rem] items-center px-[3.125rem] max-md:pt-[4.0625rem]',
                    // Fixed height rather than padding, so the pills centre on
                    // the badge's midline whatever the pill row measures.
                    RAIL_TABS_BAND,
                )}
            >
                {/* The row's height is the pill row's alone — the family line is
                    taken out of flow so its second line can't grow the nav and
                    shift the 130px gap to the content below, which has to stay
                    the same on every tab and every profile. */}
                <div className="relative flex w-full items-center">
                    {/* The pills take their natural width and never wrap. `z-20`
                        puts them above the family line: the line is out of flow,
                        so unlike the old `flex-1` layout nothing guarantees it
                        yields to them. Clearance is 211px at 1024 rising to 409px
                        at 1920 with a full 8-tab set — but a tab set wide enough
                        to reach the line should slide under it, not take its
                        clicks. */}
                    {/* One track holding the whole set, with the selection as
                        a single pane that travels between the labels — the same
                        switch the project gallery's Gallery/Grid toggle runs
                        on. Light tone: this band sits on the white profile. */}
                    <LensSwitch
                        ariaLabel="Profile sections"
                        className="relative z-20 shrink-0"
                        tone={overDark ? 'dark' : 'light'}
                        activeKey={activeTabLink}
                        items={navItems.map((item) => {
                            const to = `${base}${item.link}`.replace(
                                /\/+$/,
                                '',
                            );
                            return {
                                key: to,
                                render: (isActive: boolean) => {
                                    const tabClassName = navPill({
                                        filled: isActive,
                                        dark: overDark,
                                        inSwitch: true,
                                    });
                                    if (item.opensEnquiry) {
                                        return (
                                            <ProfessionalContactTabButton
                                                className={tabClassName}
                                            />
                                        );
                                    }
                                    return (
                                        <RoccoLink
                                            to={to}
                                            // "intent" (hover/focus), not
                                            // "render": every tab loader re-runs
                                            // the overview query, so rendering
                                            // nine prefetch links fired nine
                                            // heavy backend round-trips per page
                                            // view and starved the navigation the
                                            // user actually made.
                                            prefetch="intent"
                                            // The router's top reset stands down:
                                            // the anchor hook aligns the nav to
                                            // the viewport top on tab change,
                                            // keeping the reader at the tabs.
                                            preventScrollReset
                                            className={tabClassName}
                                        >
                                            {LABEL_OVERRIDES[item.label] ??
                                                item.label}
                                        </RoccoLink>
                                    );
                                },
                            };
                        })}
                    />
                    {/* The family line rides the pill row's right edge, under the
                    hero's logo cluster (Figma 5498:67352). Capped so it keeps
                    clear of the pills instead of running up to them.

                    The offset equals the pill height (2.837rem) − the line's own
                    line-height (1.144rem), dropping its FIRST line's bottom onto
                    the pills' bottom edge; a second line then hangs into the
                    empty spacer below. Both terms are rem, so it holds at any
                    width.

                    `z-10` is load-bearing: a second line overflows past the nav,
                    and the first section below opens a stacking context of its
                    own (`transform-gpu` on the scroll-reveal). Being later in
                    tree order, its opaque background painted straight over the
                    overflowing line. */}
                    <ProfileFamilyLine
                        className="absolute right-0 z-10 max-w-[34rem] text-right"
                        theme={overDark ? 'dark' : 'light'}
                    />
                </div>
            </div>
        </nav>
    );
};
